/**
 * A draft is one upload of the accounts: a run of consecutive `source.file` entries sharing
 * a file, the later entries re-extractions of it. Drafts are numbered by upload, and each
 * takes its state from the commits whose pins fall in its window.
 */
import type {TranslationKey} from "@tolgee/react";
import {
  DateTime,
  DisclosureAccountsCommit,
  DisclosureSupportingDocument,
  File as FileT,
  SourceMin,
  SourceProcessingState,
  SourceVersion,
  UserMin,
} from "platformed-client/types";

export type DraftVersionState = "working" | "current" | "superseded" | "never_committed";

export type DraftVersion = {
  /** The first upload is 1. */
  number: number;
  state: DraftVersionState;
  /** This is when the draft's first entry was made. */
  receivedAt: DateTime;
  uploadedBy: UserMin | null;
  /** A commit pins the draft's latest entry. */
  asOf: DateTime;
  file: FileT | null;
  pageCount: number | null;
  processingState: SourceProcessingState;
  hasLayout: boolean;
  reextracted: boolean;
  /** The current commit pins an earlier extraction of this draft than its latest. */
  reextractedSinceCommit: boolean;
};

export const VERSION_STATE_BADGE = {
  working: {label: "c.Working_copy", palette: "orange"},
  current: {label: "c.Current", palette: "green"},
  superseded: {label: "c.Superseded", palette: "gray"},
  never_committed: {label: "c.Not_committed", palette: "gray"},
} as const satisfies Record<DraftVersionState, {label: TranslationKey; palette: string}>;

const time = (at: DateTime) => new Date(at).getTime();

const within = (at: DateTime, from: DateTime, until: DateTime | null | undefined) =>
  time(at) >= time(from) && (until == null || time(at) < time(until));

const groupDrafts = (versions: SourceVersion[]): SourceVersion[][] => {
  const groups: SourceVersion[][] = [];
  for (const version of versions) {
    const group = groups.at(-1);
    if (group && (group[0].file?.file_id ?? null) === (version.file?.file_id ?? null)) group.push(version);
    else groups.push([version]);
  }
  return groups;
};

/** It is undefined for an instant before the first upload. */
export const draftNumberAt = (versions: SourceVersion[], asOf: DateTime): number | undefined => {
  const index = groupDrafts(versions).findIndex(group => within(asOf, group[0].as_of, group.at(-1)!.replaced_at));
  return index === -1 ? undefined : index + 1;
};

/** `versions` are oldest first, as the versions endpoint returns them. The result is newest first. */
export const buildDraftVersions = (versions: SourceVersion[], commits: DisclosureAccountsCommit[]): DraftVersion[] => {
  const groups = groupDrafts(versions);

  const current = commits.find(commit => commit.replaced_at == null);
  const inGroup = (commit: DisclosureAccountsCommit, group: SourceVersion[]) =>
    within(commit.source_ref.as_of, group[0].as_of, group.at(-1)!.replaced_at);

  return groups
    .map((group, index): DraftVersion => {
      const first = group[0];
      const latest = group.at(-1)!;
      const isCurrent = current != null && inGroup(current, group);
      const state: DraftVersionState = isCurrent
        ? "current"
        : commits.some(commit => inGroup(commit, group))
          ? "superseded"
          : index === groups.length - 1
            ? "working"
            : "never_committed";
      return {
        number: index + 1,
        state,
        receivedAt: first.as_of,
        uploadedBy: first.created_by ?? null,
        asOf: latest.as_of,
        file: latest.file ?? null,
        pageCount: latest.page_count ?? null,
        processingState: latest.processing_state,
        hasLayout: latest.has_layout,
        reextracted: group.length > 1,
        reextractedSinceCommit: isCurrent && time(current.source_ref.as_of) < time(latest.as_of),
      };
    })
    .reverse();
};

export type DraftDocumentRole = "primary" | "supporting";

export type DraftDocument = {
  id: string;
  role: DraftDocumentRole;
  name: string;
  file: FileT | null;
  pageCount: number | null;
  uploadedAt: DateTime;
  uploadedBy: UserMin | null;
  processingState: SourceProcessingState;
  /** Only a supporting document has a source to remove. */
  source?: SourceMin;
};

const sourceFile = (source: SourceMin): FileT | null =>
  source.kind.type === "Questionnaire" ? null : source.kind.content.file;

export const draftDocuments = (
  accounts: SourceMin,
  version: DraftVersion,
  supporting: DisclosureSupportingDocument[],
): DraftDocument[] => [
  {
    id: `accounts-${version.number}`,
    role: "primary",
    name: version.file?.name ?? accounts.name,
    file: version.file,
    pageCount: version.pageCount,
    uploadedAt: version.receivedAt,
    uploadedBy: version.uploadedBy,
    processingState: version.processingState,
  },
  ...supporting.map(({source, page_count, created_by}): DraftDocument => ({
    id: source.source_id,
    role: "supporting",
    name: source.name,
    file: sourceFile(source),
    pageCount: page_count ?? null,
    uploadedAt: source.created_at,
    uploadedBy: created_by ?? null,
    processingState: source.processing_state,
    source,
  })),
];

/** `friendly-mimes`' resolver throws for a MIME type it doesn't know. */
export type ResolveMime = (mime: string) => {fileType: string};

export const formatName = (file: FileT | null, resolveMime: ResolveMime): string => {
  if (file == null) return "—";
  try {
    return resolveMime(file.mime_type).fileType.slice(1).toUpperCase();
  } catch {
    const extension = file.name.includes(".") ? file.name.split(".").at(-1) : undefined;
    return extension ? extension.toUpperCase() : "—";
  }
};

/** A name is taken when it is in `taken` or an earlier file in the drop has it. */
export const splitDuplicates = <F extends {name: string}>(dropped: F[], taken: Iterable<string>) => {
  const names = new Set(taken);
  const accepted: F[] = [];
  const duplicates: F[] = [];
  for (const file of dropped) {
    (names.has(file.name) ? duplicates : accepted).push(file);
    names.add(file.name);
  }
  return {accepted, duplicates};
};
